/**
 * ESC/POS command encoding.
 *
 * Originally the printer here was used SOLELY as a solenoid driver. This file
 * now also encodes the text commands a barista ticket needs (T003,
 * specs/002-barista-order-tickets) — init, plain text, bold, and feed/cut —
 * alongside the drawer's own kick and status commands, because both are the
 * same physical device behind the same output boundary (FR-108).
 */

/** `ESC p m t1 t2` — pulse the drawer kick-out connector. */
export function kickCommand(pin: 2 | 5 = 2): Uint8Array {
  // m = 0 selects pin 2, m = 1 selects pin 5. t1/t2 are the pulse on/off times
  // in 2 ms units: 25 (=50 ms) on, 250 (=500 ms) off. These are the values
  // virtually every drawer datasheet specifies.
  return new Uint8Array([0x1b, 0x70, pin === 2 ? 0x00 : 0x01, 0x19, 0xfa]);
}

/** `DLE EOT 1` — transmit real-time printer status. */
export function statusQuery(): Uint8Array {
  return new Uint8Array([0x10, 0x04, 0x01]);
}

export interface PrinterStatus {
  /**
   * Drawer kick-out connector pin 3. On virtually all ESC/POS printers this
   * reads HIGH when the drawer is CLOSED.
   */
  drawerOpen: boolean;
  online: boolean;
}

/**
 * Decode the single status byte.
 *
 * Bit 2 carries the drawer signal. Support for this varies by model, so the
 * caller must capability-detect rather than assume — a wrong drawer-state alert
 * is worse than none, because staff learn to ignore it (FR-106).
 */
export function decodeStatus(byte: number): PrinterStatus {
  return {
    drawerOpen: (byte & 0b0000_0100) === 0,
    online: (byte & 0b0000_1000) === 0,
  };
}

// ---------------------------------------------------------------------------
// Ticket text commands (T003, specs/002-barista-order-tickets/research.md R3)
// ---------------------------------------------------------------------------

/** `ESC @` — reset to defaults. Sent before every ticket so a prior ticket's
 * bold state can never bleed into the next one. */
export function initPrinter(): Uint8Array {
  return new Uint8Array([0x1b, 0x40]);
}

/** `ESC E n` — bold on (n=1) / off (n=0). */
export function boldOn(): Uint8Array {
  return new Uint8Array([0x1b, 0x45, 0x01]);
}

export function boldOff(): Uint8Array {
  return new Uint8Array([0x1b, 0x45, 0x00]);
}

/**
 * Explicit substitutions for characters that plain-ASCII decomposition can't
 * reach on its own — the peso sign has no accented-Latin equivalent to strip
 * down to.
 */
const CHAR_SUBSTITUTIONS: Record<string, string> = {
  "₱": "P", // ₱
};

/**
 * Reduce text to the printable ASCII range (0x20–0x7E).
 *
 * PLAIN ASCII, NOT A CODEPAGE COMMAND (FR-209, R3). ESC/POS codepages are not
 * standardized across vendors — the same codepage number selects different
 * glyph tables on different firmware, and getting it wrong prints silently
 * wrong characters rather than failing loudly. A ticket is read for seconds;
 * legible ASCII beats a gamble on a vendor table this project has no hardware
 * to verify against.
 *
 * Accented Latin (ñ, é, ü) is stripped to its base letter via NFD
 * decomposition. The peso sign has no such decomposition, so it is
 * substituted explicitly. Anything left outside the printable range becomes
 * `?` — a visible placeholder, never a blank and never raw control bytes.
 */
export function toPrintableAscii(text: string): string {
  let result = "";
  for (const char of text) {
    result += CHAR_SUBSTITUTIONS[char] ?? char;
  }
  // Combining diacritical marks (U+0300–U+036F) — strips the accent left
  // behind once NFD has decomposed e.g. "é" into "e" + a combining acute.
  result = result.normalize("NFD").replace(/[̀-ͯ]/g, "");

  let ascii = "";
  for (let i = 0; i < result.length; i++) {
    const code = result.charCodeAt(i);
    ascii += code >= 0x20 && code <= 0x7e ? result[i] : "?";
  }
  return ascii;
}

/**
 * ASCII bytes with no trailing `LF` — for composing one printed line out of
 * several styled chunks (e.g. a bold quantity prefix followed by a plain
 * name), where only the last chunk should end the line.
 */
export function text(value: string): Uint8Array {
  const ascii = toPrintableAscii(value);
  const bytes = new Uint8Array(ascii.length);
  for (let i = 0; i < ascii.length; i++) bytes[i] = ascii.charCodeAt(i);
  return bytes;
}

export function lineFeed(): Uint8Array {
  return new Uint8Array([0x0a]);
}

/** One line of text, substituted to printable ASCII, terminated with `LF`. */
export function textLine(value: string): Uint8Array {
  const body = text(value);
  const bytes = new Uint8Array(body.length + 1);
  bytes.set(body, 0);
  bytes[body.length] = 0x0a;
  return bytes;
}

/**
 * `ESC d n` feed, then `GS V 1` partial cut where the printer supports it.
 *
 * `canCut` is capability-detected by the caller, never assumed — the same
 * discipline `capability.ts` already applies to drawer-state reporting. A
 * printer that can't cut still gets fed clear of the tear bar.
 */
export function feedAndCut(lines: number, canCut: boolean): Uint8Array {
  const feed = new Uint8Array([0x1b, 0x64, lines]);
  if (!canCut) return feed;

  const cut = new Uint8Array([0x1d, 0x56, 0x01]);
  const combined = new Uint8Array(feed.length + cut.length);
  combined.set(feed, 0);
  combined.set(cut, feed.length);
  return combined;
}
